import React, { useEffect, useMemo, useState } from 'react';
import { WhatsAppTemplate, WhatsAppTemplateStatus } from '../../types';
import { getWhatsAppTemplates } from '../../services/marketingApi';
import { StatsRow, sendStats } from '../../services/coreApi';
import TemplateForm from './TemplateForm';
import { Rendered, blanksOf, byId, isFilling, isStaff, render, shapeLabel, statusTone, thumbOf } from './shape';
import { Empty, Failed, Loading, PageFrame, Pill, Section, Tabs, Th, btn, btnTone, input, td, trClick } from '../../components/Kit';

/** Templates — every lead-facing message as one row: its picture, its shape,
 *  its Meta status and its numbers from the send ledger; a row opens the one
 *  form (view = edit = create). Staff notifications list below. Aggregates
 *  only: this portal is shared outside the company, so no send row and no
 *  lead ever renders here. */

type Sort = 'replies' | 'sends' | 'newest';
type StatusFilter = 'all' | WhatsAppTemplateStatus;

const pct = (n: number, of: number) => (of ? `${Math.round((100 * n) / of)}%` : '');
const num = (n: number) => n.toLocaleString('en-IN');
const edited = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' });

const STATUSES: WhatsAppTemplateStatus[] = ['APPROVED', 'PENDING', 'DRAFT', 'REJECTED'];

const WhatsAppTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[] | null>(null);
  const [stats, setStats] = useState<StatsRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState<string>('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('replies');
  const [form, setForm] = useState<{ open: boolean; template: WhatsAppTemplate | null }>({ open: false, template: null });

  const load = async () => {
    try {
      const [t, s] = await Promise.all([getWhatsAppTemplates(), sendStats('template')]);
      setTemplates(t.templates);
      setStats(s);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  };
  useEffect(() => { load(); }, []);

  const all = useMemo(() => (templates ? byId(templates) : {}), [templates]);
  const statsById = useMemo(() => Object.fromEntries((stats || []).map((r) => [r.key || '', r])), [stats]);
  const marketing = useMemo(() => (templates || []).filter((t) => !isStaff(t)), [templates]);
  const groups = useMemo(
    () => Array.from(new Set(marketing.map((t) => t.group).filter((g): g is string => !!g))).sort(),
    [marketing],
  );

  // Search and status apply to both lists; group only to the lead-facing one.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (t: WhatsAppTemplate) =>
      (status === 'all' || t.status === status)
      && (!q || t.name.includes(q) || t.display_name.toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q));
  }, [query, status]);

  const rows = useMemo(() => {
    const n = (t: WhatsAppTemplate) => statsById[t.id];
    return marketing
      .filter((t) => !group || t.group === group)
      .filter(matches)
      .sort((a, b) => {
        if (sort === 'newest') return b.created_at.localeCompare(a.created_at);
        const ka = sort === 'replies' ? n(a)?.replied || 0 : n(a)?.sends || 0;
        const kb = sort === 'replies' ? n(b)?.replied || 0 : n(b)?.sends || 0;
        return kb - ka || b.created_at.localeCompare(a.created_at);
      });
  }, [marketing, group, matches, sort, statsById]);
  const staff = useMemo(() => (templates || []).filter(isStaff).filter(matches), [templates, matches]);

  const totals = useMemo(() => {
    const sum = { sends: 0, replied: 0, visited: 0 };
    for (const r of stats || []) { sum.sends += r.sends; sum.replied += r.replied; sum.visited += r.visited; }
    return sum;
  }, [stats]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: marketing.length };
    for (const t of marketing) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [marketing]);

  if (error) return <PageFrame title="Templates"><Failed what="templates" error={error} /></PageFrame>;

  const statusTabs: [StatusFilter, React.ReactNode][] = [
    ['all', <>All <span className="text-graphite font-normal num">{counts.all || 0}</span></>],
    ...STATUSES.filter((s) => counts[s]).map((s): [StatusFilter, React.ReactNode] =>
      [s, <>{s.charAt(0) + s.slice(1).toLowerCase()} <span className="text-graphite font-normal num">{counts[s]}</span></>]),
  ];

  return (
    <PageFrame
      title="Templates"
      subline={stats && <span className="num"><b className="text-forest">{num(totals.visited)}</b> visits booked within 14 days · {num(totals.replied)} replies within 7 days · {num(totals.sends)} sends</span>}
      action={<button className={`${btn} ${btnTone.forest}`} onClick={() => setForm({ open: true, template: null })}>+ New template</button>}
      bar={<>
        <div className="relative">
          <svg viewBox="0 0 16 16" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-graphite pointer-events-none" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5 14 14" strokeLinecap="round" />
          </svg>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search templates"
                 className={`${input} !h-8 pl-8 w-[240px]`} />
        </div>
        <Tabs value={status} onChange={setStatus} items={statusTabs} />
        {groups.length > 0 && (
          <select value={group} onChange={(e) => setGroup(e.target.value)} className={`${input} !h-8 font-semibold`}>
            <option value="">All groups</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
        <span className="flex-1" />
        <span className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite">Sort</span>
        <Tabs value={sort} onChange={setSort} items={[['replies', 'Replies'], ['sends', 'Sends'], ['newest', 'Newest']]} />
      </>}
    >
      {!templates ? <Loading what="templates" /> : (
        <>
          <Section title={`${rows.length} message${rows.length === 1 ? '' : 's'}`} note="lead-facing">
            <table className="w-full min-w-[960px] border-collapse">
              <thead><tr>
                <Th left>Template</Th><Th left>Status</Th><Th left>Language</Th>
                <Th>Visits</Th><Th>Replied</Th><Th>Sent</Th><Th>Delivered</Th><Th>Edited</Th>
                <th className="w-6 border-b border-charcoal/10" />
              </tr></thead>
              <tbody>
                {rows.length === 0 && <Empty cols={9}>No templates match{query ? ` “${query}”` : ''}.</Empty>}
                {rows.map((t) => (
                  <Row key={t.id} t={t} parent={t.parent_id ? all[t.parent_id] : undefined} n={statsById[t.id]}
                       onOpen={() => setForm({ open: true, template: t })} />
                ))}
              </tbody>
            </table>
          </Section>

          {staff.length > 0 && (
            <Section title="Staff notifications" note={`${staff.length} utility templates · staff only`}>
              <table className="w-full min-w-[760px] border-collapse">
                <thead><tr>
                  <Th left>Template</Th><Th left>Shape</Th><Th left>Status</Th><Th left>Language</Th><Th>Edited</Th>
                  <th className="w-6 border-b border-charcoal/10" />
                </tr></thead>
                <tbody>
                  {staff.map((t) => (
                    <tr key={t.id} onClick={() => setForm({ open: true, template: t })} className={`${trClick(false)} group`}>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-[13.5px] group-hover:text-forest">{t.name}</div>
                        <div className="text-[12px] text-graphite max-w-[480px] truncate">{(t.body || '').split('\n')[0]}</div>
                      </td>
                      <td className={`${td} text-[12.5px] text-graphite`}>{shapeLabel(render(t)).replace(/^text · /, 'none · ')}</td>
                      <td className={td}><Pill tone={statusTone(t.status)}>{t.status.toLowerCase()}</Pill></td>
                      <td className={`${td} text-[12.5px] text-graphite`}>{t.language} · {t.category.toLowerCase()}</td>
                      <td className={`${td} text-right text-[12.5px] text-graphite`}>{edited(t.updated_at)}</td>
                      <td className="pr-3 text-right text-charcoal/25 group-hover:text-forest transition-colors" aria-hidden>›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </>
      )}

      {form.open && (
        <TemplateForm
          template={form.template}
          shapes={marketing.filter((t) => !isFilling(t))}
          onClose={() => setForm({ open: false, template: null })}
          onSuccess={() => { setForm({ open: false, template: null }); load(); }}
        />
      )}
    </PageFrame>
  );
};

/** One lead-facing template: picture, name and shape, status, language, then
 *  what the ledger has for it. */
const Row: React.FC<{ t: WhatsAppTemplate; parent?: WhatsAppTemplate; n?: StatsRow; onOpen: () => void }> = ({ t, parent, n, onOpen }) => {
  const r: Rendered = render(t, parent);
  const draft = t.status !== 'APPROVED';
  const blanks = !t.parent_id ? blanksOf(t.body) : [];
  const sent = !!n && n.sends > 0;
  return (
    <tr onClick={onOpen} className={`${trClick(false)} group`}>
      <td className="px-3 py-2.5">
        <div className={`flex items-center gap-3.5 min-w-0 ${draft || t.retired_at ? 'opacity-60' : ''}`}>
          <Thumb r={r} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[14px] group-hover:text-forest">{t.display_name}</span>
              {t.retired_at && <Pill>retired</Pill>}
              {t.group && <span className="text-[11px] text-graphite">{t.group}</span>}
            </div>
            <div className="text-[12px] text-graphite mt-0.5 truncate max-w-[420px]">
              {t.parent_id
                ? <>fills <span className="text-forest font-semibold">{parent?.name || t.parent_id}</span> · {shapeLabel(r)}</>
                : <>{shapeLabel(r)}{blanks.length > 0 && <> · <span className="text-amber-ink font-semibold">{blanks.map((b) => `{{${b}}}`).join(' ')} to fill</span></>}</>}
            </div>
          </div>
        </div>
      </td>
      <td className={td}><Pill tone={statusTone(t.status)}>{t.status.toLowerCase()}{t.parent_id ? ' · inherited' : ''}</Pill></td>
      <td className={`${td} text-[12.5px] text-graphite`}>{t.language} · {t.category.toLowerCase()}</td>
      {sent ? (
        <>
          <td className={`${td} text-right`}>{n!.visited ? <span className="text-forest font-semibold text-[15px]">{num(n!.visited)}</span> : <span className="text-charcoal/25">–</span>}</td>
          <td className={`${td} text-right`}>
            <span className="font-semibold text-amber-ink">{num(n!.replied)}</span>
            <span className="text-[11.5px] text-graphite ml-1">{pct(n!.replied, n!.sends)}</span>
          </td>
          <td className={`${td} text-right font-semibold`}>{num(n!.sends)}</td>
          <td className={`${td} text-right text-graphite`}>{pct(n!.delivered, n!.sends)}</td>
        </>
      ) : (
        <td colSpan={4} className="px-3 py-3 text-right text-[12.5px] text-graphite">{draft ? 'never sent' : 'no sends on the ledger'}</td>
      )}
      <td className={`${td} text-right text-[12.5px] text-graphite`}>{edited(t.updated_at)}</td>
      <td className="pr-3 text-right text-charcoal/25 group-hover:text-forest transition-colors" aria-hidden>›</td>
    </tr>
  );
};

/** A small picture of the message: header media, the carousel's first cards
 *  side by side, or a plain house-green swatch. */
const Thumb: React.FC<{ r: Rendered }> = ({ r }) => {
  const media = thumbOf(r);
  const cards = r.templateType === 'CAROUSEL' ? r.carouselCards.filter((c) => c.header_media_url).slice(0, 3) : [];
  return (
    <div className="relative flex-none w-[68px] h-[48px] rounded-md overflow-hidden bg-[#2b3a2e]">
      {cards.length > 1 ? (
        <div className="absolute inset-0 flex gap-px">
          {cards.map((c, i) => (
            <div key={i} className="flex-1 min-w-0">
              {c.header_format === 'VIDEO'
                ? <video src={`${c.header_media_url}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                : <img src={c.header_media_url} alt="" loading="lazy" className="w-full h-full object-cover" />}
            </div>
          ))}
        </div>
      ) : media ? (
        media.video
          ? <video src={`${media.url}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
          : <img src={media.url} alt="" loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#5f8a63] to-[#2f5236]" />
      )}
      {media?.video && cards.length <= 1 && (
        <span className="absolute inset-0 grid place-items-center text-warm-white/90 text-[13px]" aria-hidden>▶</span>
      )}
      {r.templateType === 'CAROUSEL' && r.carouselCards.length > 1 && (
        <span className="absolute right-0.5 bottom-0.5 text-[9px] font-bold text-warm-white bg-charcoal/70 rounded px-1 num">{r.carouselCards.length}</span>
      )}
    </div>
  );
};

export default WhatsAppTemplates;
